// src/pages/PublicProductPage.tsx
// Página pública de venda de produto — acessível sem login

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    BookOpen, Layers, FileText, CreditCard,
    ChevronDown, ChevronUp, CheckCircle, AlertCircle,
    Zap, ScrollText, Brain, MessageCircle, Copy,
    ChevronRight, Star, Target, Stethoscope, ArrowRight,
    Heart, HeartPulse, Activity, Pill, Syringe, Microscope,
    Thermometer, Dna, FlaskConical, TestTube, Baby, Bone, Scan,
    ClipboardList, Award, Lightbulb, ListChecks, GraduationCap, Eye,
    Ear, Wind, HeartCrack, Waves, Droplets, Ambulance, Siren, Cross, BriefcaseMedical,
    Sparkles
} from 'lucide-react';
import { DescBlock } from '../components/admin/products/tabs/CreateProducts';
import './PublicProductPage.css';

// ── Tipos ──────────────────────────────────────────────────────────────
interface SubdeckItem {
    _id: string;
    name: string;
    description: string;
    cardCount?: number;
    customIcon?: string | null;
    previewUrl?: string | null;
}

interface DeckData {
    _id: string;
    name: string;
    description: string;
    apostilas: SubdeckItem[];
    subdecks: SubdeckItem[];
    totalCards: number;
}

interface Totals {
    decks: number;
    apostilas: number;
    subdecks: number;
    flashcards: number;
}

interface ProductPlan {
    _id: string;
    durationMonths: number | null;
    priceCents: number;
}

interface PublicProduct {
    _id: string;
    title: string;
    description: string;
    type: string;
    plans: ProductPlan[];
    publicPage: {
        pixKey: string;
        whatsappNumber: string;
        pixHolderName: string;
        showPrice: boolean;
        showStats: boolean;
        previewEnabled: boolean;
        previewButtonText: string;
        previewButtonUrl: string;
        previewNotice: string;
        gridCols: number;
        heroDescription: string;
        statsMode: 'auto' | 'custom';
        statsCustomContent: string;
        differentialEnabled: boolean;
        differentialTitle: string;
        differentialContent: string;
        differentialRightMode: 'none' | 'animation_osce' | 'stat_cards';
        differentialStatCards: Array<{ icon: string; value: string; label: string }>;
    };
    content: {
        type: string;
        decks: DeckData[];
        totals: Totals;
    } | null;
}

// ── Mapa de ícones por nome ─────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    CheckCircle, ChevronRight, BookOpen, Zap, Star,
    Brain, FileText, Target, Stethoscope, ArrowRight,
    Layers, ScrollText,
    Heart, HeartPulse, Activity, Pill, Syringe, Microscope,
    Thermometer, Dna, FlaskConical, TestTube, Baby, Bone, Scan,
    ClipboardList, Award, Lightbulb, ListChecks, GraduationCap, Eye, Ear,
    Wind, HeartCrack, Waves, Droplets, Ambulance, Siren, Cross, BriefcaseMedical,
    Sparkles
};

// Renderiza blocos de descrição: JSON array de {type,icon?,text} ou string plana
function renderDescBlocks(raw: string): React.ReactNode {
    if (!raw) return null;
    let blocks: DescBlock[] = [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) blocks = parsed;
        else blocks = [{ type: 'p', text: raw }];
    } catch {
        blocks = [{ type: 'p', text: raw }];
    }
    return (
        <div className="ppp-desc-blocks">
            {blocks.map((b, i) => {
                if (b.type === 'p') {
                    return <p key={i} className="ppp-desc-p">{b.text}</p>;
                }
                const IconComp = b.icon ? (ICON_MAP[b.icon] ?? ChevronRight) : ChevronRight;
                return (
                    <div key={i} className="ppp-desc-li">
                        <IconComp size={13} className="ppp-desc-li-icon" />
                        <span>{b.text}</span>
                    </div>
                );
            })}
        </div>
    );
}

// ── Helpers ────────────────────────────────────────────────────────────
function formatPrice(cents: number): string {
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function formatDuration(months: number | null): string {
    if (months === null) return 'Vitalício';
    if (months === 1) return '1 mês';
    if (months < 12) return `${months} meses`;
    if (months === 12) return '1 ano';
    return `${months / 12} anos`;
}

function buildWhatsappUrl(number: string, productTitle: string): string {
    const cleaned = number.replace(/\D/g, '');
    const full = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
    const msg = encodeURIComponent(
        `Olá! Quero adquirir "${productTitle}" e estou enviando o comprovante do PIX.`
    );
    return `https://wa.me/${full}?text=${msg}`;
}

// ── Ícone WhatsApp SVG ─────────────────────────────────────────────────
const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
);

// ── Hook de animação por scroll ────────────────────────────────────────
function useScrollReveal(active: boolean) {
    useEffect(() => {
        if (!active) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach(e => {
                    if (e.isIntersecting) {
                        const delay = (e.target as HTMLElement).dataset.delay || '0';
                        (e.target as HTMLElement).style.transitionDelay = `${delay}ms`;
                        e.target.classList.add('ppp-in');
                        obs.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.08, rootMargin: '-10px 0px -10px 0px' }
        );
        requestAnimationFrame(() => {
            document.querySelectorAll('.ppp-anim').forEach(el => obs.observe(el));
        });
        return () => obs.disconnect();
    }, [active]);
}

// ── Animação OSCE interativa ────────────────────────────────────────────
const OsceAnimation: React.FC = () => {
    const [phase, setPhase] = useState<'blur' | 'clicking' | 'revealed'>('blur');

    useEffect(() => {
        const timers: ReturnType<typeof setTimeout>[] = [];
        const run = () => {
            setPhase('blur');
            timers.push(setTimeout(() => setPhase('clicking'), 2800));
            timers.push(setTimeout(() => setPhase('revealed'), 3200));
            timers.push(setTimeout(() => setPhase('blur'), 7000));
            timers.push(setTimeout(run, 7800));
        };
        const init = setTimeout(run, 400);
        return () => { clearTimeout(init); timers.forEach(clearTimeout); };
    }, []);

    return (
        <div className="ppp-osce-wrap">
            {/* Barra de título tipo app */}
            <div className="ppp-osce-titlebar">
                <div className="ppp-osce-dots"><span /><span /><span /></div>
                <span className="ppp-osce-title-text">GRAIA — Cenário Clínico</span>
            </div>
            <div className="ppp-osce-body">
                {/* Badge */}
                <div className="ppp-osce-badge">
                    <Stethoscope size={11} /> Estação OSCE · Cardiologia
                </div>

                {/* Pergunta */}
                <p className="ppp-osce-examiner-label">O examinador pergunta:</p>
                <p className="ppp-osce-question">
                    Descreva o sopro esperado: momento do ciclo cardíaco, caráter, foco de máxima intensidade e irradiação.
                </p>

                <p className="ppp-osce-hint">Responda em voz alta, depois confira:</p>

                {/* Área de resposta */}
                <div className={`ppp-osce-answer-wrap ${phase === 'revealed' ? 'ppp-revealed' : ''}`}>
                    <div className="ppp-osce-answer-label">Descrição do sopro</div>
                    <div className="ppp-osce-answer-text">
                        Sopro sistólico ejetivo, crescendo-decrescendo (em diamante), rude e granular.
                        Foco de máxima intensidade no 2° EIC direito (foco aórtico).
                        Irradia para a fúrcula esternal e carótidas.
                    </div>
                </div>

                {/* Botão / Check */}
                {phase !== 'revealed' ? (
                    <button className={`ppp-osce-btn${phase === 'clicking' ? ' ppp-osce-btn-click' : ''}`} tabIndex={-1}>
                        <Eye size={13} />
                        Ver resposta
                    </button>
                ) : (
                    <div className="ppp-osce-check">
                        <CheckCircle size={14} />
                        Confira sua resposta!
                    </div>
                )}
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════

const PublicProductPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [product, setProduct] = useState<PublicProduct | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedDecks, setExpandedDecks] = useState<Set<string>>(new Set());
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [copiedPix, setCopiedPix] = useState(false);

    useScrollReveal(!!product);

    useEffect(() => {
        if (slug) fetchProduct(slug);
    }, [slug]);

    useEffect(() => {
        document.title = product ? `${product.title} | GRAIA` : 'GRAIA';
        return () => { document.title = 'GRAIA'; };
    }, [product]);

    useEffect(() => {
        if (product?.content?.decks?.length) {
            setExpandedDecks(new Set([product.content.decks[0]._id]));
        }
    }, [product]);

    const fetchProduct = async (s: string) => {
        try {
            const res = await fetch(`/api/public/produto/${s}`);
            if (!res.ok) throw new Error('not_found');
            const data = await res.json();
            setProduct(data.product);
        } catch {
            setError('Produto não encontrado.');
        } finally {
            setLoading(false);
        }
    };

    const toggleDeck = (id: string) => {
        setExpandedDecks(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const scrollToCta = () => {
        document.getElementById('ppp-cta')?.scrollIntoView({ behavior: 'smooth' });
    };

    const copyPixKey = async (key: string) => {
        try {
            await navigator.clipboard.writeText(key);
            setCopiedPix(true);
            setTimeout(() => setCopiedPix(false), 2500);
        } catch {
            const el = document.createElement('textarea');
            el.value = key;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            setCopiedPix(true);
            setTimeout(() => setCopiedPix(false), 2500);
        }
    };

    // ── Loading ──
    if (loading) {
        return (
            <div className="ppp-loading">
                <div className="ppp-spinner" />
                <span>Carregando...</span>
            </div>
        );
    }

    // ── Error ──
    if (error || !product) {
        return (
            <div className="ppp-error">
                <div className="ppp-error-icon"><AlertCircle size={28} /></div>
                <h2>Produto indisponível</h2>
                <p>Este link não existe ou está temporariamente desativado.</p>
                <a href="/marketplace" className="ppp-error-link">Ver todos os produtos →</a>
            </div>
        );
    }

    const { content, publicPage, plans } = product;
    const totals = content?.totals;
    const decks = content?.decks ?? [];
    const hasPlans = plans && plans.length > 0 && publicPage.showPrice;
    const cheapestPlan = hasPlans ? plans.reduce((a, b) => a.priceCents < b.priceCents ? a : b) : null;
    const whatsappUrl = publicPage.whatsappNumber
        ? buildWhatsappUrl(publicPage.whatsappNumber, product.title)
        : '#';

    return (
        <div className="ppp-root">

            {/* ── HERO ── */}
            <section className="ppp-hero">
                <div className="ppp-hero-inner">

                    {/* Logo */}
                    <a href="/" className="ppp-hero-brand">
                        <img src="/graia-logo.png" alt="GRAIA" className="ppp-hero-logo" />
                    </a>

                    <h1 className="ppp-hero-title">{product.title}</h1>
                    <div className="ppp-title-mark ppp-title-mark--hero" />

                    {publicPage.heroDescription && (
                        <p className="ppp-hero-description">{publicPage.heroDescription}</p>
                    )}

                    {/* Preço + CTA na mesma linha */}
                    <div className="ppp-hero-action-row">
                        {hasPlans && plans.length === 1 && (
                            <span className="ppp-hero-price-tag">{formatPrice(plans[0].priceCents)}</span>
                        )}
                        {hasPlans && plans.length > 1 && plans.map(plan => (
                            <span key={plan._id} className="ppp-hero-price-tag">{formatPrice(plan.priceCents)}</span>
                        ))}
                        <button className="ppp-hero-cta" onClick={scrollToCta}>
                            <CheckCircle size={17} />
                            Quero adquirir
                        </button>
                    </div>

                    {/* Prévias */}
                    {publicPage.previewEnabled && (
                        <div className="ppp-hero-preview">
                            <a
                                href={publicPage.previewButtonUrl || '#'}
                                className="ppp-hero-preview-btn"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Zap size={15} />
                                {publicPage.previewButtonText || 'Acessar prévias grátis'}
                            </a>
                            {publicPage.previewNotice && (
                                <p className="ppp-hero-preview-notice">{publicPage.previewNotice}</p>
                            )}
                        </div>
                    )}

                </div>
            </section>

            {/* ── STATS ── */}
            {publicPage.showStats && (
                <section className="ppp-stats-section ppp-anim">
                    <div className="ppp-stats-inner">
                        <p className="ppp-stats-eyebrow">O que você recebe</p>

                        {/* Modo personalizado */}
                        {publicPage.statsMode === 'custom' && publicPage.statsCustomContent ? (
                            <div className="ppp-stats-custom">
                                {renderDescBlocks(publicPage.statsCustomContent)}
                            </div>
                        ) : (
                            /* Modo automático */
                            totals && (totals.decks > 0 || totals.flashcards > 0) && (
                                <div className="ppp-stats-grid">
                                    {totals.decks > 1 && (
                                        <div className="ppp-stat-card ppp-anim" data-delay="0">
                                            <div className="ppp-stat-icon ppp-stat-icon--green"><BookOpen size={18} /></div>
                                            <span className="ppp-stat-number">{totals.decks}</span>
                                            <span className="ppp-stat-label">Decks</span>
                                        </div>
                                    )}
                                    {totals.apostilas > 0 && (
                                        <div className="ppp-stat-card ppp-anim" data-delay="80">
                                            <div className="ppp-stat-icon ppp-stat-icon--amber"><ScrollText size={18} /></div>
                                            <span className="ppp-stat-number">{totals.apostilas}</span>
                                            <span className="ppp-stat-label">Apostilas</span>
                                        </div>
                                    )}
                                    {totals.subdecks > 0 && (
                                        <div className="ppp-stat-card ppp-anim" data-delay="160">
                                            <div className="ppp-stat-icon ppp-stat-icon--blue"><Layers size={18} /></div>
                                            <span className="ppp-stat-number">{totals.subdecks}</span>
                                            <span className="ppp-stat-label">Subdecks</span>
                                        </div>
                                    )}
                                    {totals.flashcards > 0 && (
                                        <div className="ppp-stat-card ppp-anim" data-delay="240">
                                            <div className="ppp-stat-icon ppp-stat-icon--purple"><Zap size={18} /></div>
                                            <span className="ppp-stat-number">{totals.flashcards.toLocaleString('pt-BR')}+</span>
                                            <span className="ppp-stat-label">Flashcards</span>
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                    </div>
                </section>
            )}

            {/* ── BLOCO DIFERENCIAL ── */}
            {publicPage.differentialEnabled && (
                <section className="ppp-differential-section ppp-anim" data-delay="0">
                    <div className="ppp-differential-inner">
                        <div className={`ppp-differential-layout ${publicPage.differentialRightMode === 'none' ? 'ppp-differential-solo' : ''}`}>
                            {/* Coluna esquerda */}
                            <div className="ppp-differential-left">
                                {publicPage.differentialTitle && (
                                    <h2 className="ppp-differential-title">{publicPage.differentialTitle}</h2>
                                )}
                                {publicPage.differentialContent && (
                                    <div className="ppp-differential-content">
                                        {renderDescBlocks(publicPage.differentialContent)}
                                    </div>
                                )}
                            </div>

                            {/* Coluna direita */}
                            {publicPage.differentialRightMode === 'animation_osce' && (
                                <div className="ppp-differential-right">
                                    <OsceAnimation />
                                </div>
                            )}
                            {publicPage.differentialRightMode === 'stat_cards' && (
                                <div className="ppp-differential-right">
                                    <div className="ppp-diff-stat-grid">
                                        {publicPage.differentialStatCards.map((card, i) => {
                                            const CardIcon = ICON_MAP[card.icon] ?? BookOpen;
                                            return (
                                                <div key={i} className="ppp-diff-stat-card ppp-anim" data-delay={`${i * 80}`}>
                                                    <div className="ppp-diff-stat-icon"><CardIcon size={18} /></div>
                                                    <span className="ppp-diff-stat-value">{card.value}</span>
                                                    <span className="ppp-diff-stat-label">{card.label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* ── CONTEÚDO ── */}
            {decks.length > 0 && (
                <section className="ppp-content-section">
                    <div className="ppp-content-inner">
                        <div className="ppp-section-header ppp-anim">
                            <h2>Conteúdo incluído</h2>
                            <p>Veja exatamente o que você está adquirindo</p>
                        </div>

                        <div className="ppp-decks-list">
                            {decks.map((deck, di) => {
                                const isExpanded = expandedDecks.has(deck._id);
                                const hasApostilas = deck.apostilas.length > 0;
                                const hasSubdecks = deck.subdecks.length > 0;

                                return (
                                    <div key={deck._id} className={`ppp-deck-card ${isExpanded ? 'expanded' : ''}`}>
                                        <div className="ppp-deck-header" onClick={() => toggleDeck(deck._id)}>
                                            <div className={`ppp-deck-index`}>{di + 1}</div>
                                            <div className="ppp-deck-header-text">
                                                <p className="ppp-deck-name">{deck.name}</p>
                                                <div className="ppp-deck-chips">
                                                    {hasApostilas && (
                                                        <span className="ppp-chip ppp-chip--amber">
                                                            <ScrollText size={10} />{deck.apostilas.length} apostil{deck.apostilas.length > 1 ? 'as' : 'a'}
                                                        </span>
                                                    )}
                                                    {hasSubdecks && (
                                                        <span className="ppp-chip ppp-chip--blue">
                                                            <Brain size={10} />{deck.subdecks.length} subdecks
                                                        </span>
                                                    )}
                                                    {deck.totalCards > 0 && (
                                                        <span className="ppp-chip ppp-chip--purple">
                                                            <Zap size={10} />{deck.totalCards.toLocaleString('pt-BR')} cards
                                                        </span>
                                                    )}
                                                </div>
                                                {deck.description && renderDescBlocks(deck.description)}
                                            </div>
                                            <ChevronDown size={17} className="ppp-deck-chevron" />
                                        </div>

                                        <div className="ppp-deck-body">
                                            <div className="ppp-deck-body-inner">

                                                {hasApostilas && (
                                                    <div className="ppp-subdeck-group">
                                                        <div className="ppp-group-label ppp-group-label--amber">
                                                            <ScrollText size={13} /> Apostilas
                                                        </div>
                                                        <div className="ppp-items-grid"
                                                            style={{ '--ppp-grid-cols': publicPage.gridCols || 3 } as React.CSSProperties}>
                                                            {deck.apostilas.map(ap => {
                                                                const ApIcon = ap.customIcon ? (ICON_MAP[ap.customIcon] ?? FileText) : FileText;
                                                                const hasDesc = !!ap.description;
                                                                const isOpen = expandedItems.has(ap._id);
                                                                return (
                                                                    <div key={ap._id} className={`ppp-item ppp-item--amber ${isOpen ? 'ppp-item-open' : ''}`}>
                                                                        <div className="ppp-item-top">
                                                                            <div className="ppp-item-icon"><ApIcon size={14} /></div>
                                                                            <p className="ppp-item-name">{ap.name}</p>
                                                                            {ap.previewUrl && (
                                                                                <a
                                                                                    href={ap.previewUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="ppp-preview-badge"
                                                                                    onClick={e => e.stopPropagation()}
                                                                                >
                                                                                    Prévia
                                                                                </a>
                                                                            )}
                                                                            {hasDesc && (
                                                                                <button
                                                                                    className="ppp-item-toggle"
                                                                                    onClick={() => setExpandedItems(prev => {
                                                                                        const n = new Set(prev);
                                                                                        n.has(ap._id) ? n.delete(ap._id) : n.add(ap._id);
                                                                                        return n;
                                                                                    })}
                                                                                    aria-label={isOpen ? 'Fechar' : 'Abrir'}
                                                                                >
                                                                                    {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        {hasDesc && isOpen && (
                                                                            <div className="ppp-item-desc">
                                                                                {renderDescBlocks(ap.description)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {hasSubdecks && (
                                                    <div className="ppp-subdeck-group">
                                                        <div className="ppp-group-label ppp-group-label--blue">
                                                            <Brain size={13} /> Subdecks de Flashcards
                                                        </div>
                                                        <div className="ppp-items-grid"
                                                            style={{ '--ppp-grid-cols': publicPage.gridCols || 3 } as React.CSSProperties}>
                                                            {deck.subdecks.map(sd => {
                                                                const SdIcon = sd.customIcon ? (ICON_MAP[sd.customIcon] ?? BookOpen) : BookOpen;
                                                                const hasDesc = !!sd.description;
                                                                const isOpen = expandedItems.has(sd._id);
                                                                return (
                                                                    <div key={sd._id} className={`ppp-item ppp-item--blue ${isOpen ? 'ppp-item-open' : ''}`}>
                                                                        <div className="ppp-item-top">
                                                                            <div className="ppp-item-icon"><SdIcon size={14} /></div>
                                                                            <p className="ppp-item-name">{sd.name}</p>
                                                                            <div className="ppp-item-top-right">
                                                                                {sd.previewUrl && (
                                                                                    <a
                                                                                        href={sd.previewUrl}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="ppp-preview-badge"
                                                                                        onClick={e => e.stopPropagation()}
                                                                                    >
                                                                                        Prévia
                                                                                    </a>
                                                                                )}
                                                                                {(sd.cardCount ?? 0) > 0 && (
                                                                                    <span className="ppp-card-count">{sd.cardCount}</span>
                                                                                )}
                                                                                {hasDesc && (
                                                                                    <button
                                                                                        className="ppp-item-toggle"
                                                                                        onClick={() => setExpandedItems(prev => {
                                                                                            const n = new Set(prev);
                                                                                            n.has(sd._id) ? n.delete(sd._id) : n.add(sd._id);
                                                                                            return n;
                                                                                        })}
                                                                                        aria-label={isOpen ? 'Fechar' : 'Abrir'}
                                                                                    >
                                                                                        {isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        {hasDesc && isOpen && (
                                                                            <div className="ppp-item-desc">
                                                                                {renderDescBlocks(sd.description)}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* ── STEPS ── */}
            <section className="ppp-steps-section ppp-anim">
                <div className="ppp-steps-inner">
                    <h2>Como obter acesso</h2>
                    <p>Processo simples em 3 passos</p>

                    <div className="ppp-steps-row">

                        <div className="ppp-step ppp-anim" data-delay="0">
                            <div className="ppp-step-num">1</div>
                            <div className="ppp-step-icon-wrap ppp-step-icon-wrap--green">
                                <CreditCard size={22} />
                            </div>
                            <div className="ppp-step-content">
                                <h3>Realize o PIX</h3>
                                {hasPlans && cheapestPlan && (
                                    <p>
                                        Transfira <strong>{formatPrice(cheapestPlan.priceCents)}</strong>
                                        {plans.length > 1 && ' ou mais'}
                                    </p>
                                )}
                                {publicPage.pixKey && (
                                    <div className="ppp-step-pix-key">{publicPage.pixKey}</div>
                                )}
                            </div>
                        </div>

                        <div className="ppp-step-connector" />

                        <div className="ppp-step ppp-anim" data-delay="120">
                            <div className="ppp-step-num">2</div>
                            <div className="ppp-step-icon-wrap ppp-step-icon-wrap--blue">
                                <MessageCircle size={22} />
                            </div>
                            <div className="ppp-step-content">
                                <h3>Envie o comprovante</h3>
                                <p>
                                    Envie o comprovante para o WhatsApp do mesmo número da chave PIX
                                    {publicPage.pixKey && <strong> ({publicPage.pixKey})</strong>}.
                                </p>
                            </div>
                        </div>

                        <div className="ppp-step-connector" />

                        <div className="ppp-step ppp-anim" data-delay="240">
                            <div className="ppp-step-num">3</div>
                            <div className="ppp-step-icon-wrap ppp-step-icon-wrap--amber">
                                <CheckCircle size={22} />
                            </div>
                            <div className="ppp-step-content">
                                <h3>Acesso liberado</h3>
                                <p>
                                    Acesso à plataforma GRAIA liberado assim que o comprovante for confirmado.
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* ── CTA FINAL ── */}
            <section className="ppp-cta-section" id="ppp-cta">
                <div className="ppp-cta-inner">

                    {/* Coluna esquerda */}
                    <div className="ppp-cta-left">
                        <p className="ppp-cta-eyebrow">Adquira agora</p>
                        <h2 className="ppp-cta-title">{product.title}</h2>
                        <div className="ppp-title-mark" />
                        {publicPage.heroDescription && (
                            <p className="ppp-cta-hero-desc">{publicPage.heroDescription}</p>
                        )}
                        <p className="ppp-cta-subtitle">
                            <span className="ppp-cta-subtitle-dot" />
                            Pague via PIX e envie o comprovante para receber o acesso em minutos.
                        </p>

                        {hasPlans && (
                            <div className="ppp-cta-prices">
                                {plans.map(plan => (
                                    <div key={plan._id} className="ppp-cta-price-wrap">
                                        <div className="ppp-cta-price-chip">
                                            <span className="ppp-cta-price-val">{formatPrice(plan.priceCents)}</span>
                                        </div>
                                        {plan.durationMonths !== null && (
                                            <span className="ppp-cta-price-dur">{formatDuration(plan.durationMonths)}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Coluna direita */}
                    <div className="ppp-cta-right">
                        {publicPage.pixKey && (
                            <div className="ppp-cta-pix-block">
                                <div className="ppp-cta-pix-header">
                                    <CreditCard size={15} />
                                    <span>Chave PIX</span>
                                </div>
                                <div className="ppp-cta-pix-key-row">
                                    <span className="ppp-cta-pix-key">{publicPage.pixKey}</span>
                                    <button
                                        className={`ppp-cta-pix-copy ${copiedPix ? 'ppp-cta-pix-copy--done' : ''}`}
                                        onClick={() => copyPixKey(publicPage.pixKey)}
                                    >
                                        {copiedPix ? <><CheckCircle size={13} /> Copiado!</> : <><Copy size={13} /> Copiar</>}
                                    </button>
                                </div>
                                {publicPage.pixHolderName && (
                                    <div className="ppp-cta-pix-holder">Titular: {publicPage.pixHolderName}</div>
                                )}
                            </div>
                        )}

                        <a
                            href={publicPage.whatsappNumber ? whatsappUrl : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ppp-cta-button"
                            style={!publicPage.whatsappNumber ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                        >
                            <WhatsAppIcon size={20} />
                            Enviar comprovante pelo WhatsApp
                        </a>

                        <p className="ppp-cta-disclaimer">
                            Acesso liberado após confirmação do comprovante.
                        </p>
                    </div>

                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer className="ppp-footer">
                <img src="/graia-logo.png" alt="GRAIA" className="ppp-footer-logo" />
                <p className="ppp-footer-brand">GRAIA</p>
                <p className="ppp-footer-text">Plataforma de estudos para Medicina</p>
            </footer>

        </div>
    );
};

export default PublicProductPage;
